import { Form } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';

import { EditBoolean } from 'components/forms/EditBoolean';
import { EditQueueReference } from 'edit/reference/EditQueueReference';
import { EditSchedulingWindowIdReference } from 'edit/reference/EditSchedulingWindowIdReference';

import type { EditableNodeDefinition } from '../../edit/EditableNodeDefinition';
import { AutoRestartCard } from '../../edit/AutoRestartCard';
import { RetentionPolicyEditor } from 'edit/retentionPolicy/RetentionPolicyEditor';
import LockSelect from '../ui/LockSelect';
import RuntimeLimits from '../ui/RuntimeLimits';
import Handles from '../ui/Handles';
import NodeTypeProperties from '../ui/NodeTypeProperties';
import Ownership from '../ui/Ownership';

interface Props {
  readonly entity: EditableNodeDefinition;
  // Bridge props still consumed by the not-yet-migrated NodeTypeProperties editor below.
  readonly state: any;
  readonly onChange: (e: any, isConfig?: boolean) => void;
}

/**
 * The Properties tab: node-level settings bound straight to the EditableNodeDefinition model, over the "Current Properties"
 * editors — all model-bound now except NodeTypeProperties, which is still on the state/setState bridge and migrates with the
 * per-type editor work.
 */
export const PropertiesTab = observer(({ entity, state, onChange }: Props) => (
  <>
    <h5 className="mb-3">Settings</h5>

    <LockSelect value={entity.locks} onChange={(locks) => (entity.locks = locks)} />

    <div className="mb-2 w-25">
      <EditSchedulingWindowIdReference
        label="Restrict to scheduling window"
        value={entity.restrictToSchedulingWindow}
        onChange={(id) => (entity.restrictToSchedulingWindow = id)}
      />
    </div>

    <div className="mb-2">
      <EditBoolean label="Hold on Submit" value={entity.holdOnSubmit} onChange={(v) => (entity.holdOnSubmit = v)} />
    </div>

    {entity.type !== 'flow' && (
      <>
        <AutoRestartCard entity={entity} />
        <RuntimeLimits entity={entity} />
      </>
    )}

    <Form.Group className="mb-2 w-full" controlId="form-Properties">
      <RetentionPolicyEditor policies={entity.retentionPolicies} onAdd={entity.createRetentionPolicy} />
      <Ownership entity={entity} />
      <div className="mb-4">
        <EditQueueReference label="Default Queue" value={entity.defaultQueuePath} onChange={(v) => (entity.defaultQueuePath = v)} />
      </div>
      <NodeTypeProperties state={state} onChange={onChange} />
      <Handles entity={entity} />
    </Form.Group>
  </>
));
